// In production (Render), set VITE_API_URL to your backend service URL
// e.g. https://pawfle-api.onrender.com
// If backend serves the frontend too, leave VITE_API_URL unset — /api works as-is
const BASE = (import.meta.env?.VITE_API_URL ?? '') + '/api';

const getToken = () => {
  try { return JSON.parse(localStorage.getItem('pawfle-token')); } catch { return null; }
};

const headers = (extra = {}) => {
  const h = { 'Content-Type': 'application/json', ...extra };
  const token = getToken();
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

const CACHE_PREFIX = 'pawfle_cache_';
const MEMORY_CACHE = new Map();

// Read from memory or sessionStorage
const readCache = (path) => {
  if (MEMORY_CACHE.has(path)) {
    return MEMORY_CACHE.get(path);
  }
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + path);
    if (raw) {
      const parsed = JSON.parse(raw);
      MEMORY_CACHE.set(path, parsed);
      return parsed;
    }
  } catch {}
  return null;
};

// Write to memory and sessionStorage
const writeCache = (path, data) => {
  const entry = { data, time: Date.now() };
  MEMORY_CACHE.set(path, entry);
  try {
    sessionStorage.setItem(CACHE_PREFIX + path, JSON.stringify(entry));
  } catch {}
};

// Invalidate all GET caches on mutation
const clearCache = () => {
  MEMORY_CACHE.clear();
  try {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => sessionStorage.removeItem(k));
  } catch {}
};

const wait = (ms) => new Promise(r => setTimeout(r, ms));

const fetchWithRetry = async (url, options, maxRetries = 2) => {
  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const res = await fetch(url, options);
      if (!res.ok && [502, 503, 504].includes(res.status) && attempt < maxRetries) {
        await wait(400 * Math.pow(2, attempt));
        continue;
      }
      return res;
    } catch (err) {
      lastError = err;
      if (attempt < maxRetries) {
        await wait(400 * Math.pow(2, attempt));
      }
    }
  }
  throw lastError || new Error('Cannot reach server — check your internet or API URL');
};

const request = async (method, path, body, options = {}) => {
  // Invalidate cache on any mutation
  if (method !== 'GET') {
    clearCache();
  }

  // Check cache for GET requests (2 minute fresh TTL, but available as stale fallback)
  const cached = method === 'GET' ? readCache(path) : null;
  const isFresh = cached && (Date.now() - cached.time < (options.ttl || 120000));

  if (isFresh && !options.skipCache) {
    return cached.data; // Return instantly
  }

  let res;
  try {
    res = await fetchWithRetry(`${BASE}${path}`, {
      method,
      headers: headers(),
      body: body ? JSON.stringify(body) : undefined,
    }, method === 'GET' ? 2 : 0);
  } catch (networkErr) {
    if (cached?.data) return cached.data;
    throw new Error('Cannot reach server — check your internet or API URL');
  }

  // If the server returned HTML instead of JSON, the backend is not connected
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    if (cached?.data) return cached.data;
    throw new Error(
      'Backend not reachable. ' +
      (import.meta.env?.VITE_API_URL
        ? `Check VITE_API_URL: ${import.meta.env.VITE_API_URL}`
        : 'Set VITE_API_URL in your Render environment variables to point to your backend service.')
    );
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (cached?.data) return cached.data;
    throw new Error(data.message || `Request failed: ${res.status}`);
  }
  
  // Save successful GET requests to cache
  if (method === 'GET') {
    writeCache(path, data);
  }

  return data;
};

const api = {
  get:    (path, options) => request('GET', path, undefined, options),
  post:   (path, body)    => request('POST', path, body),
  put:    (path, body)    => request('PUT', path, body),
  patch:  (path, body)    => request('PATCH', path, body),
  delete: (path)          => request('DELETE', path),
  getCached: (path)       => readCache(path)?.data || null,
  setCached: (path, data) => writeCache(path, data),
  clearCache,

  upload: async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    
    let res;
    try {
      const h = headers();
      delete h['Content-Type']; // Let browser set multipart boundary
      
      res = await fetch(`${BASE}/upload`, {
        method: 'POST',
        headers: h,
        body: formData,
      });
    } catch (networkErr) {
      throw new Error('Cannot reach server — check your internet or API URL');
    }
    
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || `Upload failed: ${res.status}`);
    return data;
  },

  uploadCustomerImage: async (file, fileName) => {
    const formData = new FormData();
    formData.append('image', file, fileName);
    
    let res;
    try {
      const h = headers();
      delete h['Content-Type']; // Let browser set multipart boundary
      
      res = await fetch(`${BASE}/upload/customer-image`, {
        method: 'POST',
        headers: h,
        body: formData,
      });
    } catch (networkErr) {
      throw new Error('Cannot reach server — check your internet or API URL');
    }
    
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || `Upload failed: ${res.status}`);
    return data;
  },

  auth: {
    register:       (data)  => api.post('/auth/register', data),
    verifyEmail:    (token) => api.post(`/auth/verify-email/${token}`),
    google:         (token) => api.post('/auth/google', { token }),
    login:          (data)  => api.post('/auth/login', data),
    getMe:          ()      => api.get('/auth/me'),
    updateProfile:  (data)  => api.put('/auth/profile', data),
    changePassword: (data)  => api.put('/auth/password', data),
    forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
    resetPassword:  (token, password) => api.post(`/auth/reset-password/${token}`, { password }),
    manageAddresses:(data)  => api.put('/auth/addresses', data),
  },

  account: {
    getStats: () => request('GET', '/account/stats'),
  },

  products: {
    list: (params, options) => api.get(`/products?${new URLSearchParams(params).toString()}`, options),
    getCachedList: (params) => api.getCached(`/products?${new URLSearchParams(params).toString()}`),
    get: (slug, options) => api.get(`/products/${slug}`, options),
    getCached: (slug) => api.getCached(`/products/${slug}`),
    getRecentReviews: () => api.get('/products/reviews/recent'),
    review: (slug, data) => api.post(`/products/${slug}/reviews`, data),
    flagReview: (slug, reviewId, data) => api.post(`/products/${slug}/reviews/${reviewId}/flag`, data),
    // admin
    adminList:   (params = {}) => api.get(`/products/admin?${new URLSearchParams(params)}`),
    create: (data)        => api.post('/products/admin', data),
    update: (id, data)    => api.put(`/products/${id}`, data),
    delete: (id)          => api.delete(`/products/${id}`),
  },

  orders: {
    create:  (data) => api.post('/orders', data),
    myOrders:()     => api.get('/orders/my'),
    get:     (id)   => api.get(`/orders/${id}`),
    cancel:  (id)   => api.put(`/orders/${id}/cancel`),
    // admin
    adminList:  (params = {}) => api.get(`/orders/admin?${new URLSearchParams(params)}`),
    adminGet:   (id)          => api.get(`/orders/admin/${id}`),
    adminUpdateStatus:(id, data) => api.put(`/orders/admin/${id}/status`, data),
    adminBulkStatus: (data)   => api.put('/orders/admin/bulk-status', data),
    adminRefund: (id, data)   => api.post(`/orders/admin/${id}/refund`, data),
    adminAddNote: (id, text)  => api.put(`/orders/admin/${id}/notes`, { text }),
    adminStats: ()            => api.get('/orders/admin/stats'),
    // PDF downloads with admin token attached
    invoiceUrl: (id) => {
      const token = getToken();
      return `${BASE}/orders/admin/${id}/invoice${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    },
    shippingLabelUrl: (id) => {
      const token = getToken();
      return `${BASE}/orders/admin/${id}/shipping-label${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    },
    generateShippingLabel: (id) => api.post(`/orders/admin/${id}/generate-shipping-label`),
  },

  payment: {
    getConfig: () => api.get('/payment/config'),
    createOrder: (data) => api.post('/payment/create-order', data),
    verify: (data) => api.post('/payment/verify', data),
  },

  users: {
    list:   (params = {}) => api.get(`/users?${new URLSearchParams(params)}`),
    stats:  ()            => api.get('/users/stats'),
    get:    (id)          => api.get(`/users/${id}`),
    update: (id, data)    => api.put(`/users/${id}`, data),
    delete: (id)          => api.delete(`/users/${id}`),
  },

  dashboard: {
    stats:           ()             => api.get('/admin/dashboard/stats'),
    salesTrend:      (period)       => api.get(`/admin/dashboard/sales-trend?period=${period}`),
    lowStock:        ()             => api.get('/admin/dashboard/low-stock'),
    pendingOrders:   ()             => api.get('/admin/dashboard/pending-orders'),
    pendingReviews:  ()             => api.get('/admin/dashboard/pending-reviews'),
    topProducts:     ()             => api.get('/admin/dashboard/top-products?range=7d'),
    moderateReview:  (productId, reviewId, status) =>
      api.patch(`/admin/reviews/${productId}/${reviewId}/moderate`, { status }),
    updateOrderStatus: (id, status) => api.put(`/orders/admin/${id}`, { status }),
  },

  adminProducts: {
    list:         (params = {}) => api.get(`/admin/products?${new URLSearchParams(params)}`),
    get:          (id)          => api.get(`/admin/products/${id}`),
    create:       (data)        => api.post('/admin/products', data),
    update:       (id, data)    => api.put(`/admin/products/${id}`, data),
    delete:       (id, hard)    => api.delete(`/admin/products/${id}${hard ? '?hard=true' : ''}`),
    bulkUpdate:   (updates)     => api.post('/admin/products/bulk-update', { updates }),
    exportCSV:    ()            => api.get('/admin/products/export-csv'),
    importPreview:(rows)        => api.post('/admin/products/import-csv/preview', { rows }),
    importCommit: (rows)        => api.post('/admin/products/import-csv/commit', { rows }),
    uploadImages: async (files) => {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) formData.append('images', files[i]);
      const res = await fetch(`${BASE}/admin/products/upload`, { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: formData });
      if (!res.ok) throw new Error('Upload failed');
      return res.json();
    }
  },

  adminCategories: {
    tree:   ()         => api.get('/admin/categories/tree'),
    list:   ()         => api.get('/admin/categories'),
    create: (data)     => api.post('/admin/categories', data),
    update: (id, data) => api.put(`/admin/categories/${id}`, data),
    delete: (id)       => api.delete(`/admin/categories/${id}`),
    reorder: (updates) => api.post('/admin/categories/reorder', { updates })
  },
  adminInventory: {
    history: (params) => api.get(`/admin/inventory/stock-history?${new URLSearchParams(params).toString()}`),
    adjust: (data) => api.post('/admin/inventory/adjust', data)
  },
  adminCustomers: {
    list: (params = {}) => api.get(`/admin/customers?${new URLSearchParams(params)}`),
    get: (id) => api.get(`/admin/customers/${id}`),
    updateSegment: (id, tags) => api.patch(`/admin/customers/${id}/segment`, { tags }),
    block: (id, reason) => api.patch(`/admin/customers/${id}/block`, { reason }),
    unblock: (id) => api.patch(`/admin/customers/${id}/unblock`),
    resetPassword: (id) => api.post(`/admin/customers/${id}/reset-password`),
    refund: (id, data) => api.post(`/admin/customers/${id}/refund`, data),
    addNote: (id, text) => api.post(`/admin/customers/${id}/notes`, { text })
  },
  adminReviews: {
    list: (params = {}) => api.get(`/admin/reviews?${new URLSearchParams(params)}`),
    flagged: () => api.get('/admin/reviews/flagged'),
    moderate: (productId, reviewId, status) => api.patch(`/admin/reviews/${productId}/${reviewId}/moderate`, { status }),
    moderateImage: (productId, reviewId, imageIndex, approved) => api.patch(`/admin/reviews/${productId}/${reviewId}/moderate-image`, { imageIndex, approved }),
    reply: (productId, reviewId, text) => api.post(`/admin/reviews/${productId}/${reviewId}/reply`, { text }),
    resolveFlag: (productId, reviewId, action) => api.patch(`/admin/reviews/${productId}/${reviewId}/resolve-flag`, { action }),
  },
  adminPromotions: {
    getCoupons: () => api.get('/admin/promotions/coupons'),
    createCoupon: (data) => api.post('/admin/promotions/coupons', data),
    updateCoupon: (id, data) => api.put(`/admin/promotions/coupons/${id}`, data),
    deleteCoupon: (id) => api.delete(`/admin/promotions/coupons/${id}`),
    
    getAutoRules: () => api.get('/admin/promotions/auto-rules'),
    createAutoRule: (data) => api.post('/admin/promotions/auto-rules', data),
    updateAutoRule: (id, data) => api.put(`/admin/promotions/auto-rules/${id}`, data),
    deleteAutoRule: (id) => api.delete(`/admin/promotions/auto-rules/${id}`),
    
    getFlashSales: () => api.get('/admin/promotions/flash-sales'),
    createFlashSale: (data) => api.post('/admin/promotions/flash-sales', data),
    updateFlashSale: (id, data) => api.put(`/admin/promotions/flash-sales/${id}`, data),
    deleteFlashSale: (id) => api.delete(`/admin/promotions/flash-sales/${id}`),
    
    getBundles: () => api.get('/admin/promotions/bundles'),
    createBundle: (data) => api.post('/admin/promotions/bundles', data),
    updateBundle: (id, data) => api.put(`/admin/promotions/bundles/${id}`, data),
    deleteBundle: (id) => api.delete(`/admin/promotions/bundles/${id}`),
  },
  cart: {
    previewDiscount: (data) => api.post('/cart/preview-discount', data)
  },
  
  marketing: {
    // Admin
    getBanners: () => api.get('/admin/marketing/banners'),
    createBanner: (data) => api.post('/admin/marketing/banners', data),
    updateBanner: (id, data) => api.put(`/admin/marketing/banners/${id}`, data),
    deleteBanner: (id) => api.delete(`/admin/marketing/banners/${id}`),
    
    getAnnouncements: () => api.get('/admin/marketing/announcements'),
    createAnnouncement: (data) => api.post('/admin/marketing/announcements', data),
    updateAnnouncement: (id, data) => api.put(`/admin/marketing/announcements/${id}`, data),
    deleteAnnouncement: (id) => api.delete(`/admin/marketing/announcements/${id}`),
    
    getPopups: () => api.get('/admin/marketing/popups'),
    createPopup: (data) => api.post('/admin/marketing/popups', data),
    updatePopup: (id, data) => api.put(`/admin/marketing/popups/${id}`, data),
    deletePopup: (id) => api.delete(`/admin/marketing/popups/${id}`),

    // Public
    getActiveBanners: () => api.get('/marketing/banners'),
    getActiveAnnouncement: () => api.get('/marketing/announcements'),
    getActivePopups: () => api.get('/marketing/popups'),
    getActiveCoupons: () => api.get('/marketing/coupons')
  },
  
  blog: {
    // Admin
    getAdminPosts: () => api.get('/blog/admin'),
    getAdminPost: (id) => api.get(`/blog/admin/${id}`),
    createPost: (data) => api.post('/blog/admin', data),
    updatePost: (id, data) => api.put(`/blog/admin/${id}`, data),
    deletePost: (id) => api.delete(`/blog/admin/${id}`),

    // Public
    getPosts: (params = {}) => api.get(`/blog?${new URLSearchParams(params)}`),
    getPost: (slug) => api.get(`/blog/slug/${slug}`)
  },

  newsletter: {
    // Admin
    getSubscribers: (params = {}) => api.get(`/newsletter/admin?${new URLSearchParams(params)}`),
    exportCSV: () => `${BASE}/newsletter/admin/export`, // Export URL

    // Public
    subscribe: (data) => api.post('/newsletter/subscribe', data),
    unsubscribe: (data) => api.post('/newsletter/unsubscribe', data)
  },

  campaigns: {
    // Admin
    getCampaigns: () => api.get('/admin/campaigns'),
    createCampaign: (data) => api.post('/admin/campaigns', data),
    sendCampaign: (id) => api.post(`/admin/campaigns/${id}/send`)
  },

  contact: {
    // Admin
    getMessages: () => api.get('/contact/admin'),
    updateMessageStatus: (id, status) => api.put(`/contact/admin/${id}`, { status }),
    deleteMessage: (id) => api.delete(`/contact/admin/${id}`),
    
    // Public
    submitMessage: (data) => api.post('/contact', data)
  }
};

export default api;
